/**
 * OAuth Callback Handler
 * Handles the redirect from Keycloak after authentication
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { authService } from '../services/authService.ts';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false); // Prevent double execution in React StrictMode

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution (React StrictMode calls useEffect twice in dev)
      if (hasProcessed.current) {
        console.log('[OAuthCallback] Already processed, skipping...');
        return;
      }
      hasProcessed.current = true;

      try {
        console.log('[OAuthCallback] Processing OAuth callback...');

        // Handle the OAuth callback
        const user = await authService.handleCallback();

        if (user) {
          console.log('[OAuthCallback] Authentication successful');

          // Get return URL from state (if provided)
          const returnUrl = (user.state as any)?.returnUrl || '/';

          // Navigate to the return URL or home
          navigate(returnUrl, { replace: true });
        } else {
          setError('Authentication failed - no user returned');
        }
      } catch (err: any) {
        console.error('[OAuthCallback] Error processing callback:', err);
        setError(err.message || 'Authentication failed');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
          padding: 4,
        }}
      >
        <Alert
          severity="error"
          sx={{
            maxWidth: 500,
            borderRadius: 2,
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">{error}</Typography>
          <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
            Redirecting to login page...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 4,
          padding: 6,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
          textAlign: 'center',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Completing Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials...
        </Typography>
      </Box>
    </Box>
  );
};

export default OAuthCallback;
